import {
  forwardRef,
  useEffect,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { CloseCircle, FilterSearch, Sort } from 'iconsax-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@design-system/dropdown-menu';
import { cn } from '@design-system/helpers';
import { Search } from 'lucide-react';

const DEBOUNCE_MS = 300;

/** Search + Filter combined bar and Sort — shared by every omni list. */
export function OmniListToolbar({
  search,
  filter,
  sort,
}: {
  search: ReactNode;
  filter: ReactNode;
  sort: ReactNode;
}) {
  return (
    <div
      data-slot='omni-list-toolbar'
      className='flex shrink-0 items-center gap-2 px-4'
    >
      <div className='flex h-10 min-w-0 flex-1 overflow-hidden rounded-xl border border-primary-200'>
        {search}
        {filter}
      </div>
      {sort}
    </div>
  );
}

/** Left half of the search+filter control. Commits `value` after a short debounce. */
export function OmniSearchField({
  value,
  onCommit,
  placeholder,
  'aria-label': ariaLabel,
}: {
  value: string;
  onCommit: (value: string) => void;
  placeholder: string;
  'aria-label': string;
}) {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (draft === value) return;
    const timer = setTimeout(() => onCommit(draft.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  return (
    <div className='flex min-w-0 flex-1 items-center gap-2 bg-primary-30 py-2.5 pl-4 pr-3'>
      <Search
        size={16}
        className='shrink-0 text-gray-800'
        aria-hidden='true'
      />
      <input
        type='search'
        role='searchbox'
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        className='min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-500 [&::-webkit-search-cancel-button]:appearance-none'
      />
      {draft ? (
        <button
          type='button'
          aria-label='Clear search'
          onClick={() => {
            setDraft('');
            onCommit('');
          }}
          className='shrink-0 text-gray-400 hover:text-gray-600'
        >
          <CloseCircle
            size={16}
            aria-hidden='true'
          />
        </button>
      ) : null}
    </div>
  );
}

type OmniFilterTriggerProps = ComponentProps<'button'> & { open?: boolean };

/** Right half of the search+filter control. Use as a PopoverTrigger child. */
export const OmniFilterTrigger = forwardRef<
  HTMLButtonElement,
  OmniFilterTriggerProps
>(function OmniFilterTrigger({ open, className, ...props }, ref) {
  return (
    <button
      ref={ref}
      type='button'
      aria-label='Filter'
      className={cn(
        'flex h-full shrink-0 items-center gap-2 border-l border-primary-200 bg-primary-50 px-3 text-sm font-medium text-gray-800 hover:bg-primary-100',
        open && 'text-primary-1000',
        className
      )}
      {...props}
    >
      <FilterSearch
        size={20}
        variant={open ? 'Bold' : 'TwoTone'}
        aria-hidden='true'
      />
      Filter
    </button>
  );
});

function SortRadio({ selected }: { selected: boolean }) {
  return (
    <span
      aria-hidden='true'
      className={cn(
        'size-4 shrink-0 rounded-full bg-gray-50',
        selected ? 'border-4 border-primary-1000' : 'border-gray-300'
      )}
    />
  );
}

/** Sort-by popover — white disc, thick primary ring when selected. */
export function OmniSortMenu<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (value: T) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type='button'
          aria-label='Sort'
          className='flex size-10 shrink-0 items-center justify-center rounded-xl border border-primary-200 text-gray-600 hover:bg-primary-30'
        >
          <Sort
            size={24}
            aria-hidden='true'
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='end'
        className='min-w-[180px] rounded-lg border-primary-200 p-0 shadow-md'
      >
        <DropdownMenuLabel className='px-4 py-2 text-sm font-normal text-gray-500'>
          Sort by
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={(next) => onChange(next as T)}
        >
          {options.map((option) => {
            const selected = value === option.value;
            return (
              <DropdownMenuRadioItem
                key={option.value}
                value={option.value}
                className={cn(
                  'h-10 cursor-pointer gap-2 rounded-none px-4 py-2 pl-4 text-sm text-gray-900',
                  'focus:bg-primary-50 data-[state=checked]:font-medium',
                  '[&>span:first-child]:hidden'
                )}
              >
                <SortRadio selected={selected} />
                {option.label}
              </DropdownMenuRadioItem>
            );
          })}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
